import React, { FC, memo, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Section } from "@/components/Section";
import { TabSelect, TTabOption } from "@/components/Tabs";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { ELegalDocumentType, fetchLegalDocuments, ILegalDocument } from "@/data/legal-documents";
import { Col, Flex, Row } from "@/eworkitComponents";
import { HtmlRenderer } from "@/eworkitMove/HtmlRenderer";
import { useFormats } from "@/hooks/Formats";
import { useRouter } from "@/router/useRouter";
import { Container } from "@/views/components/Container";
import { PublicLayout } from "@/views/components/Layout";
import { StyledContent } from "@/views/components/Page";

import { LegalInformationTab } from "./components/LegalInformationTab";
import { ILegalInformationTypeDetailRouteProps, ILegalInformationTypeMatchParam } from "./types";

import "./_legal-information.scss";

interface Props extends ILegalInformationTypeDetailRouteProps {}

const LegalInformationComponent: FC<Props> = memo(() => {
  const { t } = useTranslation("public");
  const { formatDate } = useFormats();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const [activeTab, setActiveTab] = useState<string>();
  const [legalDocuments, setLegalDocuments] = useState([] as ILegalDocument[]);
  const activeLegalDocument = legalDocuments.find(l => l.type === activeTab);
  const { searchParams } = useRouter<any, ILegalInformationTypeMatchParam>();

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchLegalDocuments();
      setLegalDocuments(response);

      if (Object.keys(ELegalDocumentType).some(k => k === searchParams.type)) {
        setActiveTab(searchParams.type);
      } else {
        setActiveTab(response[0].type);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(ELegalDocumentType).some(k => k === searchParams.type)) {
      setActiveTab(searchParams.type);
    }
  }, [searchParams]);

  const tabSelectOptions: TTabOption[] = [
    {
      type: "group",
      label: t("LEGAL_INFORMATION.TITLE"),
      options: legalDocuments.map(({ type }) => ({
        type: "option",
        value: type,
        label: t(`LEGAL_INFORMATION.TABS.${type}.CATEGORY`),
      })),
    },
  ];
  return (
    <>
      <Helmet>
        <title>{t(`META.${activeTab}.TITLE`)}</title>
        <meta name="description" content={t(`META.${activeTab}.DESCRIPTION`)} />
      </Helmet>
      <PublicLayout
        hideTopbar={searchParams.variant === "mobile"}
        hideFooter={searchParams.variant === "mobile"}
        borderBottomTopbar={false}
        stickyTopbar>
        <Container padding={["sm"]}>
          {isSmallScreen && searchParams.variant !== "mobile" && (
            <Section borderRadius padding={["lg"]} innerMargin="md" margin={["md", false]}>
              <TabSelect
                pathName="legal-information"
                routeParam="type"
                options={tabSelectOptions}
                onChange={option => setActiveTab(option)}
              />
            </Section>
          )}
          <StyledContent padding={[false]} innerPadding={false} innerBorder={false} className="legal-information">
            <Row innerBorder>
              {!isSmallScreen && searchParams.variant !== "mobile" && (
                <Col width={{ sm: 4, xxs: 12 }}>
                  <Flex direction="column" padding={["lg"]}>
                    <>
                      <Flex padding={[false, false, "md"]}>
                        <Text size="xl" weight="bold">
                          {t(`LEGAL_INFORMATION.TITLE`)}
                        </Text>
                      </Flex>
                      {legalDocuments.map(({ type }) => {
                        return (
                          <LegalInformationTab
                            active={activeTab === type}
                            category={t(`LEGAL_INFORMATION.TABS.${type}.CATEGORY`)}
                            key={type}
                            onTabClick={() => setActiveTab(type)}
                          />
                        );
                      })}
                    </>
                  </Flex>
                </Col>
              )}
              <Col width={{ sm: 8, xxs: 12 }}>
                <Flex padding={["md"]} grow={1} direction="column">
                  {activeLegalDocument && (
                    <>
                      <Flex justify="flex-start" padding={["md", false]}>
                        <Text align="left" color="grey2">
                          {t(`LEGAL_INFORMATION.UPDATE_DATE`, {
                            value: formatDate(activeLegalDocument.modifiedDate),
                          })}
                        </Text>
                      </Flex>
                      <HtmlRenderer html={activeLegalDocument.content} />
                    </>
                  )}
                </Flex>
              </Col>
            </Row>
          </StyledContent>
        </Container>
      </PublicLayout>
    </>
  );
});

export const LegalInformation = LegalInformationComponent;
